import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from "react-native";
import React, { useEffect, useState, useMemo } from "react";
import { useStateContext } from "../lib/context";
import EnuCollection from "../components/nuCollection.js";
import { Ionicons } from "@expo/vector-icons";
import Cart from "../components/Cart";

// Strip accents and uppercase for accent-insensitive comparison
const normalize = (str) =>
  String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();

const Store = ({ navigation, route }) => {
  const [nusearch, setNusearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [typeGrid, setTypeGrid] = useState(true);
  const [real, setReal] = useState(route.params);
  const [ogData] = useState(route.params);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(Math.ceil(route.params.length / 24));

  // Context
  const { scroll } = useStateContext();

  // Multi-word tokenized + accent-insensitive filter
  const filterProducts = (query) => {
    if (!query) return ogData;
    const tokens = normalize(query).split(/\s+/).filter(Boolean);
    return ogData.filter((item) => {
      const name = normalize(item.DENOMINACION);
      const code = normalize(item.ARTICULO__ID);
      return tokens.every((t) => name.includes(t) || code.includes(t));
    });
  };

  // Sort: full-name prefix > word-boundary prefix > position of first token
  const sortResults = (results, query) => {
    if (!query) return results;
    const first = normalize(query.split(/\s+/)[0]);
    return [...results].sort((a, b) => {
      const aName = normalize(a.DENOMINACION);
      const bName = normalize(b.DENOMINACION);
      const aFull = aName.startsWith(normalize(query));
      const bFull = bName.startsWith(normalize(query));
      if (aFull !== bFull) return aFull ? -1 : 1;
      const aWord = aName.startsWith(first);
      const bWord = bName.startsWith(first);
      if (aWord !== bWord) return aWord ? -1 : 1;
      return aName.indexOf(first) - bName.indexOf(first);
    });
  };

  // Top 6 suggestion labels (distinct product names from current results)
  const suggestions = useMemo(() => {
    if (nusearch.length < 2) return [];
    const filtered = filterProducts(nusearch);
    const seen = new Set();
    const out = [];
    for (const item of filtered) {
      if (out.length >= 6) break;
      const label = item.DENOMINACION;
      if (!seen.has(label)) {
        seen.add(label);
        out.push(label);
      }
    }
    return out;
  }, [nusearch]);

  useEffect(() => {
    if (nusearch === "") {
      setReal(ogData);
      setPages(Math.ceil(ogData.length / 24));
    } else {
      const results = sortResults(filterProducts(nusearch), nusearch);
      setReal(results);
      setPages(Math.ceil(results.length / 24));
      setPage(1);
    }
  }, [nusearch]);

  const PaginationControl = () => (
    <View style={localStyles.paginationContainer}>
      <TouchableOpacity
        style={[localStyles.pageButton, page === 1 && localStyles.pageButtonDisabled]}
        onPress={() => (page > 1 ? setPage(page - 1) : null)}
        disabled={page === 1}
      >
        <Ionicons name="chevron-back" size={24} color={page === 1 ? "#ccc" : "#2a2a8e"} />
      </TouchableOpacity>
      
      <View style={localStyles.pageInfo}>
        <Text style={localStyles.pageText}>Página {page}</Text>
        <Text style={localStyles.pageTotal}>de {pages}</Text>
      </View>

      <TouchableOpacity
        style={[localStyles.pageButton, page >= pages && localStyles.pageButtonDisabled]}
        onPress={() => (page < pages ? setPage(page + 1) : null)}
        disabled={page >= pages}
      >
        <Ionicons name="chevron-forward" size={24} color={page >= pages ? "#ccc" : "#2a2a8e"} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={localStyles.container}>
      {/* Header */}
      <View style={localStyles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={localStyles.backButton}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={localStyles.headerTitle}>Catálogo</Text>
        <Cart />
      </View>

      <ScrollView 
        style={localStyles.scrollView} 
        contentContainerStyle={localStyles.scrollContent}
        scrollEnabled={scroll}
        showsVerticalScrollIndicator={false}
      >
        {/* Search Bar */}
        <View style={localStyles.searchContainer}>
          <View style={localStyles.searchWrapper}>
            <Ionicons name="search" size={20} color="#8F959E" />
            <TextInput
              onChangeText={(t) => { setNusearch(t); setShowSuggestions(true); }}
              value={nusearch}
              style={localStyles.searchInput}
              placeholder="Buscar productos..."
              placeholderTextColor="#8F959E"
              autoFocus={false}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              returnKeyType="search"
            />
            {nusearch.length > 0 && (
              <TouchableOpacity onPress={() => { setNusearch(""); setShowSuggestions(false); }}>
                <Ionicons name="close-circle" size={18} color="#ccc" />
              </TouchableOpacity>
            )}
          </View>

          {/* Autocomplete suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <View style={localStyles.suggestionsBox}>
              {suggestions.map((label) => (
                <TouchableOpacity
                  key={label}
                  style={localStyles.suggestionItem}
                  onPress={() => { setNusearch(label); setShowSuggestions(false); }}
                >
                  <Ionicons name="search-outline" size={14} color="#999" style={{ marginRight: 8 }} />
                  <Text style={localStyles.suggestionText} numberOfLines={1}>{label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Controls Bar */}
        <View style={localStyles.controlsBar}>
          <Text style={localStyles.resultsText}>
            {real.length} productos
          </Text>
          
          <View style={localStyles.viewToggles}>
            <TouchableOpacity 
              style={[localStyles.toggleButton, !typeGrid && localStyles.toggleActive]}
              onPress={() => setTypeGrid(false)}
            >
              <Ionicons 
                name="list" 
                size={20} 
                color={!typeGrid ? "#2a2a8e" : "#999"} 
              />
            </TouchableOpacity>
            <TouchableOpacity 
              style={[localStyles.toggleButton, typeGrid && localStyles.toggleActive]}
              onPress={() => setTypeGrid(true)}
            >
              <Ionicons 
                name="grid" 
                size={20} 
                color={typeGrid ? "#2a2a8e" : "#999"} 
              />
            </TouchableOpacity>
          </View>
        </View>

        <PaginationControl />

        {/* Products Grid/List */}
        <View style={localStyles.productsContainer}>
          <EnuCollection products={real} type={!typeGrid} pagination={page} />
        </View>

        {pages > 1 && <PaginationControl />}
        
        <View style={{height: 40}} /> 
      </ScrollView>
    </View>
  );
};

export default Store;

const localStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F4F6FD",
  },
  header: {
    backgroundColor: "#2a2a8e",
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    flexDirection: "row",
    alignItems: "center",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  backButton: {
    padding: 4,
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#fff",
    flex: 1, // Added flex: 1 to push Cart to the right if needed, though Cart handles its own layout
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  searchContainer: {
    padding: 20,
    paddingBottom: 10,
  },
  searchWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 50,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    borderWidth: 1,
    borderColor: "#eee",
  },
  searchInput: {
    flex: 1,
    marginLeft: 10,
    fontSize: 16,
    color: "#333",
  },
  suggestionsBox: {
    backgroundColor: "#fff",
    borderRadius: 10,
    marginTop: 6,
    borderWidth: 1,
    borderColor: "#eee",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 4,
    overflow: "hidden",
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f5f5f5",
  },
  suggestionText: {
    fontSize: 14,
    color: "#333",
    flex: 1,
  },
  controlsBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 20,
    marginBottom: 16,
  },
  resultsText: {
    fontSize: 14,
    color: "#666",
    fontWeight: "500",
  },
  viewToggles: {
    flexDirection: "row",
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 2,
    borderWidth: 1,
    borderColor: "#eee",
  },
  toggleButton: {
    padding: 8,
    borderRadius: 6,
  },
  toggleActive: {
    backgroundColor: "#E8EAF6",
  },
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginVertical: 10,
    gap: 20,
  },
  pageButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  pageButtonDisabled: {
    backgroundColor: "#f5f5f5",
    elevation: 0,
  },
  pageInfo: {
    alignItems: "center",
  },
  pageText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
  },
  pageTotal: {
    fontSize: 12,
    color: "#999",
  },
  productsContainer: {
    minHeight: 200,
  },
});
